import { useEffect, useRef, useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import styles from "./UnitPriceForm.module.css";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import UnitPriceConfirmation from "./UnitPriceConfirmation";
import FieldInfo from "../MarketPlaceDetail/FieldInfo";
import { FIELD_INFO } from "../MarketPlaceDetail/fieldInfoContent";

const T2RangeEdit = ({
  allow_delete,
  unitPrices,
  onSave,
  adminVendorId,
  productVendorId,
  inline = false,
}) => {
  const [showCard, setShowCard] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [unitPriceErrors, setUnitPriceErrors] = useState({});
  const [unitPriceForms, setUnitPriceForms] = useState([
    {
      type: "new",
      StartRange: "0",
      EndRange: "",
      Steps: "1",
    },
  ]);
  // FIX 3: these credit-tier rows carry no DB id (rangeData strips it), so we
  // can't tell "just-added" from "existing DB tier" by id. Instead snapshot how
  // many tiers existed on first DB load — anything at or beyond that index is a
  // freshly-added tier the admin may always delete, even on a locked plan.
  // rangeData loads async, so capture on the FIRST non-empty hydrate (an effect,
  // not render-time, to avoid latching the empty initial value).
  const originalRangeCountRef = useRef(null);
  useEffect(() => {
    if (
      originalRangeCountRef.current === null &&
      unitPrices &&
      unitPrices.length
    ) {
      originalRangeCountRef.current = unitPrices.length;
    }
  }, [unitPrices]);

  useEffect(() => {
    if (unitPrices && unitPrices.length) {
      const withIds = unitPrices.map((u, i) => ({
        ...u,
        type: "existing",
        changed_unitprice: u.changed_unitprice ?? false,
      }));
      setUnitPriceForms(withIds);
    }
  }, [showCard, unitPrices]);

  const addNewForm = () => {
    const newForm = {
      type: "new",
      StartRange: unitPriceForms[unitPriceForms.length - 1].EndRange,
      EndRange: "",
      Steps: "1",
    };
    const next = [...unitPriceForms, newForm];
    setUnitPriceForms(next);
    commitInline(next);
  };

  const validateUnitPriceForms = (forms) => {
    const errors = {};
    let isValid = true;

    forms.forEach((form, index) => {
      const rowErrors = {};
      const requiredFields = ["StartRange", "EndRange", "Steps"];
      requiredFields.forEach((field) => {
        const value = form[field];
        if (value === "" || value === null || value === undefined) {
          rowErrors[field] = true;
          isValid = false;
        }
      });
      const start = parseFloat(form["StartRange"]);
      const end = parseFloat(form["EndRange"]);
      if (end <= start) {
        // previously this marked the field red but forgot isValid = false,
        // letting inverted ranges through to save
        rowErrors["EndRange"] = true;
        isValid = false;
      }
      const steps = Number(form["Steps"]);
      if (
        !Number.isInteger(steps) ||
        steps < 1 ||
        (Number.isFinite(end - start) && steps > end - start)
      ) {
        rowErrors["Steps"] = true;
        isValid = false;
      }
      if (Object.keys(rowErrors).length > 0) {
        errors[index] = rowErrors;
      }
    });
    if (Object.keys(errors).length) {
      toast.error(
        "Fix range fields: End Range must exceed Start Range and Steps must be a whole number between 1 and the range size",
      );
      setUnitPriceErrors(errors);
    }
    return isValid;
  };

  const markChangedUnits = (forms = unitPriceForms) => {
    return forms.map((curr, i) => {
      const original = unitPrices[i];

      // compare between old and new unit_price
      const changed =
        !original ||
        curr.StartRange !== original.StartRange ||
        curr.EndRange !== original.EndRange ||
        curr.Steps !== original.Steps;

      // return true if any changes in unit_price
      return {
        ...curr,
        changed_unitprice: changed,
      };
    });
  };

  // LIVE-COMMIT (inline mode): sync the credit tier ranges/steps up on every
  // edit so the parent re-derives each plan's unit_ids immediately and the outer
  // "Save" always has the latest — no separate "Apply ranges" click. Not gated
  // on validation; errors surface inline and the outer Save runs its own gate.
  const commitInline = (forms) => {
    if (!inline) return;
    onSave(markChangedUnits(forms));
  };

  const handleSave = () => {
    const isValid = validateUnitPriceForms(unitPriceForms);
    if (!isValid) {
      return;
    }
    //Calculate changes in required field
    const updatedUnits = markChangedUnits();

    // Inline: ranges already committed live on change. Button is a
    // validate/confirm affordance.
    if (inline) {
      onSave(updatedUnits);
      toast.success("Ranges applied");
      return;
    }

    const changedSomething = updatedUnits.some((u) => u.changed_unitprice);
    if (!allow_delete && changedSomething) {
      setShowConfirmation(true);
      return;
    } else {
      setShowCard(false);
      onSave(updatedUnits);
      return;
    }
  };

  const handleSaveFromConfirmation = () => {
    const updatedUnits = markChangedUnits();
    setShowConfirmation(false);
    setShowCard(false);
    onSave(updatedUnits);
    return;
  };

  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const realValue =
      type === "checkbox"
        ? checked
        : type === "number"
          ? value === ""
            ? ""
            : Number(value)
          : value;
    let updatedForms;
    if (name === "EndRange") {
      updatedForms = unitPriceForms.map((form, i) => {
        if (i === index) {
          return { ...form, EndRange: realValue };
        }

        if (i === index + 1) {
          return { ...form, StartRange: realValue };
        }

        return form;
      });
    } else {
      updatedForms = unitPriceForms.map((form, i) =>
        i === index ? { ...form, [name]: realValue } : form,
      );
    }
    setUnitPriceForms(updatedForms);
    setUnitPriceErrors((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [name]: false,
      },
    }));
    commitInline(updatedForms);
  };

  const deleteHandler = (index) => {
    const updatedForms = unitPriceForms.filter((val, i) => i !== index);
    setUnitPriceForms(updatedForms);
    commitInline(updatedForms);
  };

  const bodyInner =
    unitPriceForms.length !== 0 ? (
      <>
        {unitPriceForms.map((form, index) => (
          <Form
            key={index}
            className={styles.card}
            onSubmit={(e) => e.preventDefault()}
          >
            <div className={styles.unitPriceTop}>
              <h1 className={styles.heading}>Range {index + 1}</h1>
              {index === unitPriceForms.length - 1 &&
              index !== 0 &&
              // FIX 3: allow deleting a just-added tier (index at/beyond the
              // original DB count) even on a locked plan; pre-existing DB tiers
              // still require allow_delete. Before the count is captured, fall
              // back to allow_delete only (never expose delete on existing DB
              // tiers of a locked plan).
              (allow_delete ||
                (originalRangeCountRef.current !== null &&
                  index >= originalRangeCountRef.current)) &&
              adminVendorId === productVendorId ? (
                <Button
                  variant="danger"
                  disabled={index === 0}
                  onClick={() => deleteHandler(index)}
                  className={styles.deleteButton}
                >
                  Delete
                </Button>
              ) : (
                ""
              )}
            </div>
            <div className={styles.cardBody}>
              <Row>
                <Col md={4}>
                  <Form.Group controlId={`formStartRange-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      Start Range
                      <FieldInfo text={FIELD_INFO["unit.start_range"]} />
                    </Form.Label>
                    <Form.Control
                      readOnly
                      required
                      type="number"
                      name="StartRange"
                      value={form.StartRange}
                      onChange={(e) => handleChange(index, e)}
                      onWheel={(e) => e.target.blur()}
                      placeholder="Start Range"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.StartRange
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formEndRange-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      End Range (Must be Greater than Start Range)
                      <FieldInfo text={FIELD_INFO["unit.end_range"]} />
                    </Form.Label>
                    <Form.Control
                      required
                      type="number"
                      name="EndRange"
                      value={form.EndRange}
                      onChange={(e) => handleChange(index, e)}
                      onWheel={(e) => e.target.blur()}
                      placeholder="End Range"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.EndRange
                          ? styles.inputError
                          : ""
                      }`}
                      readOnly={adminVendorId !== productVendorId}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formSteps-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      Steps (Can't be more that the diff of StartRange &amp;
                      EndRange i.e:{" "}
                      {parseInt(form.EndRange) - parseInt(form.StartRange)} )
                      <FieldInfo text={FIELD_INFO["unit.steps"]} />
                    </Form.Label>
                    <Form.Control
                      required
                      type="number"
                      name="Steps"
                      value={form.Steps}
                      onChange={(e) => handleChange(index, e)}
                      onWheel={(e) => e.target.blur()}
                      placeholder="Steps"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Steps ? styles.inputError : ""
                      }`}
                      readOnly={adminVendorId !== productVendorId}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Form>
        ))}
        {adminVendorId === productVendorId && (
          <>
            <Button className={styles.addPriceUnit} onClick={addNewForm}>
              + Add range
            </Button>
            <Button className={styles.submitBtn} onClick={handleSave}>
              {inline ? "Apply ranges" : "Submit"}
            </Button>
          </>
        )}
      </>
    ) : (
      <p>No Unit Price available</p>
    );

  return (
    <div className={styles.container}>
      {!inline && (
        <button
          className={styles.EditUnitPrice}
          onClick={(e) => {
            e.preventDefault();
            setShowCard(true);
          }}
          aria-haspopup="dialog"
          aria-expanded={showCard}
        >
          <FaEdit />
        </button>
      )}
      {inline ? (
        <div className={styles.inlineUnitBody}>
          {!allow_delete && (
            <div className={styles.activeSubWarning}>
              <span aria-hidden="true">⚠</span>
              <span>
                This plan has active subscriptions — changing these tiers will
                re-price existing customers at their next renewal.
              </span>
            </div>
          )}
          {bodyInner}
        </div>
      ) : (
        showCard && (
          <div className={styles.unitPriceContainer}>
            <div
              className={styles.dropShadow}
              onClick={() => setShowCard(false)}
              aria-hidden="true"
            />
            <div
              className={styles.unitPricefield}
              role="dialog"
              aria-modal="true"
              aria-label="Unit Prices"
              onKeyDown={(e) => {
                if (e.key === "Escape") setShowCard(false);
              }}
            >
              <div className={styles.unitBody}>{bodyInner}</div>
            </div>
          </div>
        )
      )}
      {showConfirmation && (
        <UnitPriceConfirmation
          onClose={() => setShowConfirmation(false)}
          onSave={() => {
            handleSaveFromConfirmation();
          }}
        />
      )}
    </div>
  );
};

export default T2RangeEdit;
